import {
	ApplicationService,
	EventService,
	GamedayApiService,
	HomeService,
	SyncService,
	SyncServiceFactory
} from "@prestosports/prestostats-core/Services";
import { EventStatus } from "@prestosports/prestostats-core/Common";
import { setCurrentTeams } from "../teams/actions";
import {
	CLEAR_EVENTS_COMPLETE,
	GET_COMPLETE_UPCOMING_EVENTS,
	GET_COMPLETE_UPCOMING_EVENTS_COMPLETE,
	GET_COMPLETED_EVENTS,
	GET_COMPLETED_EVENTS_COMPLETE,
	GET_EVENT_DEFAULT_GAME_CONFIGURATION,
	GET_EVENT_DEFAULT_GAME_CONFIGURATION_COMPLETE,
	GET_EVENTS,
	GET_EVENTS_COMPLETE,
	GET_EVENTS_SUMMARY,
	GET_EVENTS_SUMMARY_COMPLETE,
	GET_IN_PROGRESS_EVENTS,
	GET_IN_PROGRESS_EVENTS_COMPLETE,
	GET_UPCOMING_EVENTS,
	GET_UPCOMING_EVENTS_COMPLETE,
	OVER_WRITE_EVENT_DATA,
	OVER_WRITE_EVENT_DATA_COMPLETE,
	RESET_CURRENT_EVENT,
	RESET_EVENT_DATA,
	RESET_EVENT_DATA_COMPLETE,
	RESTORE_EVENT_DEFAULT_GAME_CONFIGURATION,
	RESTORE_EVENT_DEFAULT_GAME_CONFIGURATION_COMPLETE,
	SAVE_EVENT,
	SAVE_EVENT_COMPLETE,
	SAVE_EVENT_DEFAULT_GAME_CONFIGURATION,
	SAVE_EVENT_DEFAULT_GAME_CONFIGURATION_COMPLETE,
	SELECT_EVENT_STREAM,
	SELECT_EVENT_STREAM_COMPLETE,
	SET_REMOTE_EVENT_CHANGED,
	SET_RULE_SET_ID_COMPLETE,
	SET_SELECTED_EVENT,
	SET_SELECTED_EVENT_COMPLETE,
	UPDATE_CURRENT_EVENT_COMPLETE,
	VALIDATION_ERROR
} from "./constants";
import GamedayApiClient from "../../services/gameday-api-client";
// eslint-disable-next-line import/no-cycle
import { setCurrentSeasonById } from "../season/actions";
import { clearGame, loadGameData, setCurrentPlayers } from "../game/actions";
import { setStreamEventId } from "../stream/actions";
import Alert from "../../util/alert";
import {
	getApiAccess,
	getCurrentEventSync,
	setSelectedDate as setSelectedDateAction,
	setSelectedSport as setSelectedSportAction,
	withQueuedSync,
	withSubmitting
} from "../app/actions";
import { CommonRoutes } from "../../routes/common-routes";
import { AppRoutes } from "../../routes/app-routes";
import { emptyPage } from "./reducer";
import { updateGameContext } from "../game-context/actions";

GamedayApiService.setGamedayApiClient(GamedayApiClient);

export const getEvents = async (dispatch) => {
	dispatch({ type: GET_EVENTS });

	const events = await GamedayApiService.getEvents();
	dispatch({
		type: GET_EVENTS_COMPLETE,
		payload: { events: events && events.map((e) => e && e.toJSON()) }
	});
};

export const getCompletedAndUpcomingEvents = async (
	seasonId,
	mergeOptions,
	filterOptions,
	pagingOptions,
	dispatch
) => {
	dispatch({ type: GET_COMPLETE_UPCOMING_EVENTS });
	const [upcomingEvents, completedEvents, inProgressEvents] = await Promise.all([
		GamedayApiService.getUpcomingEvents(
			seasonId,
			mergeOptions,
			filterOptions,
			pagingOptions
		),
		GamedayApiService.getCompletedEvents(
			seasonId,
			mergeOptions,
			filterOptions,
			pagingOptions
		),
		GamedayApiService.getInProgressEvents(
			seasonId,
			mergeOptions,
			filterOptions,
			pagingOptions
		)
	]);

	dispatch({
		type: GET_COMPLETE_UPCOMING_EVENTS_COMPLETE,
		payload: {
			upcomingEvents: {
				...upcomingEvents,
				data: upcomingEvents?.data?.map((e) => e && e.toJSON())
			},
			completedEvents: {
				...completedEvents,
				data: completedEvents?.data?.map((e) => e && e.toJSON())
			},
			inProgressEvents: {
				...inProgressEvents,
				data: inProgressEvents?.data?.map((e) => e && e.toJSON())
			}
		}
	});
};

export const getUpcomingEvents = async (
	seasonId,
	mergeOptions,
	filterOptions,
	pagingOptions,
	dispatch
) => {
	dispatch({ type: GET_UPCOMING_EVENTS });
	const upcomingEvents = await GamedayApiService.getUpcomingEvents(
		seasonId,
		mergeOptions,
		filterOptions,
		pagingOptions
	);

	dispatch({
		type: GET_UPCOMING_EVENTS_COMPLETE,
		payload: {
			upcomingEvents: {
				...upcomingEvents,
				data: upcomingEvents?.data?.map((e) => e && e.toJSON())
			}
		}
	});
};

export const getCompletedEvents = async (
	seasonId,
	mergeOptions,
	filterOptions,
	pagingOptions,
	dispatch
) => {
	dispatch({ type: GET_COMPLETED_EVENTS });
	const completedEvents = await GamedayApiService.getCompletedEvents(
		seasonId,
		mergeOptions,
		filterOptions,
		pagingOptions
	);

	dispatch({
		type: GET_COMPLETED_EVENTS_COMPLETE,
		payload: {
			completedEvents: {
				...completedEvents,
				data: completedEvents?.data?.map((e) => e && e.toJSON())
			}
		}
	});
};

export const getInProgressEvents = async (
	seasonId,
	mergeOptions,
	filterOptions,
	pagingOptions,
	dispatch
) => {
	dispatch({ type: GET_IN_PROGRESS_EVENTS });
	const inProgressEvents = await GamedayApiService.getInProgressEvents(
		seasonId,
		mergeOptions,
		filterOptions,
		pagingOptions
	);

	dispatch({
		type: GET_IN_PROGRESS_EVENTS_COMPLETE,
		payload: {
			inProgressEvents: {
				...inProgressEvents,
				data: inProgressEvents?.data?.map((e) => e && e.toJSON())
			}
		}
	});
};

export const clearEvents = (dispatch) => {
	dispatch({
		type: CLEAR_EVENTS_COMPLETE,
		payload: {
			events: emptyPage,
			upcomingEvents: emptyPage,
			inProgressEvents: emptyPage,
			completedEvents: emptyPage,
			currentEvent: null
		}
	});
};

export const getEventsSummary = async (date, sportCodes, dispatch) => {
	dispatch({
		type: GET_EVENTS_SUMMARY,
		payload: {
			upcomingEvents: emptyPage,
			inProgressEvents: emptyPage,
			completedEvents: emptyPage
		}
	});

	const events = await HomeService.getEventsSummary(date, sportCodes);
	dispatch({
		type: GET_EVENTS_SUMMARY_COMPLETE,
		payload: {
			eventsSummary: {
				events
			}
		}
	});
};

export const updateCurrentEvent = async (event, dispatch) => {
	dispatch({
		type: UPDATE_CURRENT_EVENT_COMPLETE,
		payload: { currentEvent: event && event.toJSON() }
	});
};

export const setCurrentEvent = async (eventId, dispatch) => {
	dispatch({ type: SET_SELECTED_EVENT });

	const { event, eventTeams, gameContext } =
		await ApplicationService.setCurrentEvent(eventId);
	const { teams, seasonId, sportId, startDateTime } = event;

	await Promise.all([
		setCurrentSeasonById(seasonId, dispatch),
		setCurrentTeams(teams, seasonId, dispatch),
		setCurrentPlayers(teams, sportId, dispatch),
		getApiAccess(eventTeams, dispatch),
		updateGameContext(gameContext, dispatch),
		setSelectedDateAction(new Date(startDateTime), dispatch),
		setSelectedSportAction(sportId, dispatch),
		setStreamEventId(eventId, dispatch)
	]);

	// TODO: Can this be part of the promise all?
	const currentEventSettings = await EventService.getGameConfigurationOrDefault(
		event.id
	);

	dispatch({
		type: SET_SELECTED_EVENT_COMPLETE,
		payload: { currentEvent: event && event.toJSON(), currentEventSettings }
	});

	return event;
};

export const saveGameConfiguration = async (eventId, gameConfig, dispatch) => {
	try {
		dispatch({
			type: SAVE_EVENT_DEFAULT_GAME_CONFIGURATION
		});
		// Core also syncs the live game clock with the saved runningClock
		// flag and returns the updated context (null when no game exists).
		const { gameContext, ...currentEventSettings } =
			await EventService.saveGameConfiguration(eventId, gameConfig);
		if (gameContext) {
			updateGameContext(gameContext, dispatch);
		}

		Alert.success("Event saved");
		dispatch({
			type: SAVE_EVENT_DEFAULT_GAME_CONFIGURATION_COMPLETE,
			payload: { currentEventSettings }
		});
	} catch (error) {
		dispatch({
			type: VALIDATION_ERROR,
			payload: { validationErrors: [error] }
		});
	}
};

// Update only the flag zone-to-gain spots mid-game (from the Update Field
// screen). Persists to the event config + live game and bypasses the
// after-start edit guard (EventService.saveFirstDownYardSet). No toast — it
// rides along with the Update Field play save.
export const saveFirstDownYardSet = async (eventId, spots, dispatch) => {
	try {
		const currentEventSettings = await EventService.saveFirstDownYardSet(
			eventId,
			spots
		);
		dispatch({
			type: SAVE_EVENT_DEFAULT_GAME_CONFIGURATION_COMPLETE,
			payload: { currentEventSettings }
		});
	} catch (error) {
		dispatch({
			type: VALIDATION_ERROR,
			payload: { validationErrors: [error] }
		});
	}
};

export const setRuleSetId = async (event, ruleSetId, dispatch) => {
	const { gameContext, ...currentEventSettings } =
		await EventService.setRuleSetId(event.id, ruleSetId);
	updateGameContext(gameContext, dispatch);
	dispatch({
		type: SET_RULE_SET_ID_COMPLETE,
		payload: { currentEventSettings }
	});
};

export const getGameConfiguration = async (eventId, dispatch) => {
	dispatch({
		type: GET_EVENT_DEFAULT_GAME_CONFIGURATION
	});

	const currentEventSettings = await EventService.getGameConfigurationOrDefault(
		eventId
	);
	dispatch({
		type: GET_EVENT_DEFAULT_GAME_CONFIGURATION_COMPLETE,
		payload: { currentEventSettings }
	});
};

export const restoreDefaultGameConfiguration = async (eventId, dispatch) => {
	dispatch({
		type: RESTORE_EVENT_DEFAULT_GAME_CONFIGURATION
	});

	// Restoring defaults goes through the same save path, so it also syncs
	// the live game clock with the restored runningClock flag.
	const { gameContext, ...currentEventSettings } =
		await EventService.restoreDefaultGameConfiguration(eventId);
	if (gameContext) {
		updateGameContext(gameContext, dispatch);
	}
	dispatch({
		type: RESTORE_EVENT_DEFAULT_GAME_CONFIGURATION_COMPLETE,
		payload: { currentEventSettings }
	});
};

const updateEvent = async (event, dispatch) => {
	dispatch({
		type: SAVE_EVENT
	});

	const saved = await GamedayApiService.saveOrUpdateEvent(event);
	Alert.success("Event saved");
	dispatch({
		type: SAVE_EVENT_COMPLETE,
		payload: { currentEvent: saved && saved.toJSON() }
	});
};

export const saveEvent = async (event, dispatch) => {
	if (EventStatus.isStarted(event)) {
		withQueuedSync(event.id, () => updateEvent(event, dispatch), dispatch);
	} else {
		updateEvent(event, dispatch);
	}
};

export const setGameInProgress = async (eventId, dispatch) =>
	withSubmitting(async () => {
		// IN_PROGRESS status code is -1 (see EventStatusList InProgress comparator)
		const event = await EventService.setEventStatus(eventId, -1);
		const saved = await GamedayApiService.saveOrUpdateEvent(event);
		await updateCurrentEvent(saved, dispatch);
	}, dispatch);

export const setRemoteEventChanged = (value, dispatch) => {
	dispatch({
		type: SET_REMOTE_EVENT_CHANGED,
		payload: { remoteEventChanged: value }
	});
};

export const overWriteEventData = async (eventId, dispatch) => {
	withSubmitting(async () => {
		dispatch({ type: OVER_WRITE_EVENT_DATA });

		const { sportId } = await EventService.getById(eventId);
		try {
			await SyncServiceFactory.getInstance(sportId).overWriteEventData(eventId);
		} catch (error) {
			Alert.error(error.message);
		}

		dispatch({
			type: OVER_WRITE_EVENT_DATA_COMPLETE,
			payload: { remoteEventChanged: false }
		});

		await loadGameData(eventId, dispatch);
		await getCurrentEventSync(eventId, dispatch);
	}, dispatch);
};

export const resetEventData = async (eventId, redirect, dispatch) => {
	withSubmitting(async () => {
		dispatch({
			type: RESET_EVENT_DATA,
			payload: {
				isLoading: true
			}
		});

		if (!eventId) {
			redirect(CommonRoutes.NOT_FOUND.url);
		} else {
			await SyncService.resetEventData(eventId);
			redirect(AppRoutes.HOME.url);
		}

		dispatch({
			type: RESET_EVENT_DATA_COMPLETE,
			payload: {}
		});

		const gameContext = await EventService.getGameContextByEventId(eventId);
		updateGameContext(gameContext, dispatch);

		clearGame(dispatch);
	}, dispatch);
};

export const resetCurrentEvent = (dispatch) => {
	dispatch({
		type: RESET_CURRENT_EVENT,
		payload: { currentEvent: null }
	});
};

export const selectEventStream = async (eventId, streamId, dispatch) => {
	dispatch({
		type: SELECT_EVENT_STREAM
	});

	const saved = await EventService.setSelectedStreamId(eventId, streamId);
	dispatch({
		type: SELECT_EVENT_STREAM_COMPLETE,
		payload: { currentEvent: saved && saved.toJSON() }
	});
};
